import { useSuspenseQuery } from "@tanstack/react-query";
import type { Locale } from "date-fns";
import { useMemo } from "react";
import { match } from "@formatjs/intl-localematcher";
import { meApi } from "@/api/me/me-api";
import { isoDate } from "@/lib/utils";
import type { Salon } from "@/types/me/api-types/me-types";

// Refetched each minute (and on window focus) so `today` moves on at the salon's midnight.
const TODAY_REFRESH_MS = 60_000;

const DATE_FNS_LOCALES = "/node_modules/date-fns/locale/";

/** The date-fns locale file that best matches the salon's locale (CLDR best fit: `zh-Hant-TW` → `zh-TW`). */
export const bestLocaleFile = (tag: string, files: string[]): string => {
  // Sorted, so a tie (`pt-PT` is as close to `pt` as to `pt-BR`) resolves the same way wherever the list comes from.
  const tags = files.map((file) => file.slice(DATE_FNS_LOCALES.length, -".js".length)).sort();
  return `${DATE_FNS_LOCALES}${match([tag], tags, "en-US")}.js`;
};

export const useSalon = (): Salon =>
  useSuspenseQuery({
    queryKey: ["me"],
    queryFn: meApi.getMe,
    select: (me) => me.salon,
    refetchInterval: TODAY_REFRESH_MS,
  }).data;

export const useSalonToday = (): Date => {
  const { today } = useSalon();
  return useMemo(() => isoDate(today), [today]);
};

/** The salon's date-fns locale for the date pickers. Vite splits one chunk per locale. */
export const useSalonDateFnsLocale = (): Locale => {
  const { locale } = useSalon();
  return useSuspenseQuery({
    queryKey: ["date-fns-locale", locale],
    queryFn: async () => {
      const loaders = import.meta.glob<Locale>(
        ["/node_modules/date-fns/locale/*.js", "!**/cdn*.js", "!**/types.js"],
        { import: "default" },
      );
      return loaders[bestLocaleFile(locale, Object.keys(loaders))]();
    },
    staleTime: Infinity,
  }).data;
};
